/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Grouping utilities for the agenda sidebar.
 *
 * All functions are pure and side-effect free — they depend only on their
 * arguments, making them trivially testable.
 */

import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { MemberType } from '@pages/authenticated/agency-pages/AgenciesContext';
import {
  SELECTABLE_PALETTE,
  RESERVED_COLORS,
  VISITS_COLOR,
  calendarColor,
} from './calendar-display';

/**
 * Whether a calendar belongs to the current user (personal, non-institutional).
 *
 * @param calendar The calendar to check.
 * @param userEmail The current user's email (case-insensitive comparison).
 */
export const isMyCalendar = (calendar: Calendar, userEmail: string): boolean =>
  calendar.owner_type === 'user' &&
  calendar.owner_id?.toLowerCase() === userEmail.toLowerCase();

/**
 * Returns all agency members excluding the current user.
 *
 * @param members Full member list from `agency.members`.
 * @param userEmail The current user's email (case-insensitive comparison).
 */
export const otherMembers = (
  members: MemberType[],
  userEmail: string
): MemberType[] =>
  members.filter(
    (m) => m.email.toLowerCase() !== userEmail.toLowerCase()
  );

/**
 * Human-readable display name for a member: "First Last", trimmed.
 *
 * @param member Agency member.
 */
export const memberDisplayName = (member: MemberType): string =>
  [member.first_name, member.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

/**
 * Colour identifying a member across the agenda.
 *
 * The member's own calendar is the source of truth, so their sidebar swatch
 * and their event chips — which are painted from that same calendar — always
 * agree. Without a calendar to read from, the colour is assigned by position
 * in a stable roster instead of by hashing the email: a hash collides well
 * before the palette runs out (two of seven members already shared a colour),
 * while positional assignment only repeats once there are more members than
 * colours.
 *
 * @param email Member email.
 * @param calendars Calendars visible to the current user.
 * @param roster All member emails, used for positional fallback.
 * @returns The member's colour.
 */
export const memberColor = (
  email: string,
  calendars: Calendar[] = [],
  roster: string[] = [],
): string => memberColors(roster.length ? roster : [email], calendars).get(email)
  ?? SELECTABLE_PALETTE[0];

export { VISITS_COLOR };

/**
 * Lowercases and trims a colour string for palette comparison.
 */
const normalizeColor = (color: string): string => color.trim().toLowerCase();

/**
 * Finds the calendar a member owns, ignoring their mirrored Google one.
 */
const ownCalendarOf = (email: string, calendars: Calendar[]): Calendar | undefined =>
  calendars.find(
    (c) => c.owner_type === 'user' && c.owner_id === email && !c.sync?.google,
  );

/**
 * Assigns members their own calendar's colour, recording each as used.
 *
 * @param roster Every member email.
 * @param calendars Calendars visible to the signed-in user.
 * @param assigned Email→colour map, filled in place.
 * @param used Normalized colours already taken, added to in place.
 */
const assignOwnCalendarColors = (
  roster: string[],
  calendars: Calendar[],
  assigned: Map<string, string>,
  used: Set<string>,
): void => {
  for (const email of roster) {
    const own = ownCalendarOf(email, calendars);
    if (!own) continue;
    const color = calendarColor(own);
    assigned.set(email, color);
    used.add(normalizeColor(color));
  }
};

/**
 * Assigns every still-unassigned member a colour from the free pool,
 * generating distinct ones once the pool runs out.
 *
 * @param roster Every member email.
 * @param assigned Email→colour map, filled in place; existing keys are kept.
 * @param pool The palette colours no member has claimed.
 */
const assignPooledColors = (
  roster: string[],
  assigned: Map<string, string>,
  pool: string[],
): void => {
  let next = 0;
  for (const email of [...roster].sort()) {
    if (assigned.has(email)) continue;
    assigned.set(email, pool[next] ?? generatedColor(next - pool.length));
    next += 1;
  }
};

/**
 * Assigns one distinct colour to every member on the roster.
 *
 * Colour is the only thing tying a sidebar row to its chips on the grid, so a
 * repeat makes two people indistinguishable. Members who own a calendar keep
 * its colour — that is what paints their events, and the two must agree.
 * Everyone else draws from the palette entries nobody has claimed, including
 * the reserved ones (the institutional wine and the Visitas layer), which is
 * what a per-member hash or a plain positional index could not account for.
 *
 * @param roster Every member email, including the signed-in user.
 * @param calendars Calendars visible to the signed-in user.
 * @param reserved Colours no member may take.
 * @returns Map from member email to colour.
 */
export const memberColors = (
  roster: string[],
  calendars: Calendar[] = [],
  reserved: readonly string[] = RESERVED_COLORS,
): Map<string, string> => {
  const assigned = new Map<string, string>();
  const used = new Set(reserved.map(normalizeColor));

  assignOwnCalendarColors(roster, calendars, assigned, used);

  const pool = SELECTABLE_PALETTE.filter((c) => !used.has(normalizeColor(c)));
  assignPooledColors(roster, assigned, pool);
  return assigned;
};

/**
 * Colour for a member past the end of the curated palette.
 *
 * Steps the hue by the golden angle so successive colours land as far apart as
 * possible, at the palette's muted saturation and lightness. Keeps the
 * one-colour-per-member guarantee on teams larger than the palette rather than
 * wrapping around and handing two people the same swatch.
 *
 * @param index Zero-based position past the end of the palette.
 * @returns A CSS colour distinct from the previous ones.
 */
const generatedColor = (index: number): string =>
  `hsl(${Math.round((index * 137.508 + 21) % 360)} 34% 33%)`;
